import { Injectable, NgZone, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Clarity from '@microsoft/clarity';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ClarityService {
  private isInitialized = false;

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Initializes Microsoft Clarity with the project ID from environment
   * or a custom project ID passed as parameter.
   * Runs outside Angular zone to avoid triggering unnecessary change detection cycles.
   */
  init(customProjectId?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isInitialized) {
      console.warn('[ClarityService] Clarity is already initialized.');
      return;
    }

    const projectId = customProjectId || (environment as any).clarityProjectId;

    if (!projectId || projectId.trim() === '' || projectId === 'YOUR_CLARITY_PROJECT_ID') {
      console.warn('[ClarityService] Project ID is missing or not configured.');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        Clarity.init(projectId);
        this.isInitialized = true;
        console.info(`[ClarityService] Microsoft Clarity initialized successfully.`);
      } catch (error) {
        console.error('[ClarityService] Failed to initialize Microsoft Clarity:', error);
      }
    });
  }

  /**
   * Associate a user ID, session ID, and friendly name with the current Clarity session
   */
  identify(customId: string, customSessionId?: string, customPageId?: string, friendlyName?: string): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.identify(customId, customSessionId, customPageId, friendlyName);
    } catch (error) {
      console.error('[ClarityService] Error identifying user in Clarity:', error);
    }
  }

  /**
   * Set custom tag key-value pairs in Clarity
   */
  setTag(key: string, value: string | string[]): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error('[ClarityService] Error setting Clarity tag:', error);
    }
  }

  /**
   * Track custom events in Clarity
   */
  event(eventName: string): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error('[ClarityService] Error sending Clarity event:', error);
    }
  }

  /**
   * Upgrades the current session to a higher priority recording (e.g. for checkout or critical flow)
   */
  upgrade(reason: string): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.upgrade(reason);
    } catch (error) {
      console.error('[ClarityService] Error upgrading Clarity session:', error);
    }
  }

  /**
   * Manage user consent for Clarity cookies
   */
  consent(hasConsent: boolean = true): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.consent(hasConsent);
    } catch (error) {
      console.error('[ClarityService] Error setting Clarity consent:', error);
    }
  }

  /**
   * Manage user consent with granular options (Consent v2)
   */
  consentV2(consentOptions?: { ad_Storage: 'granted' | 'denied'; analytics_Storage: 'granted' | 'denied' }): void {
    if (!this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    try {
      Clarity.consentV2(consentOptions);
    } catch (error) {
      console.error('[ClarityService] Error setting Clarity consentV2:', error);
    }
  }

  /**
   * Check if Clarity has been initialized
   */
  getStatus(): boolean {
    return this.isInitialized;
  }
}
